import React from 'react'

import { Box, Button, TextareaAutosize } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import SchoolIcon from '@mui/icons-material/School'

import MessageList from './MessageList'
import KnowledgeBasePicker, { KnowledgeBaseButton } from './KnowledgeBasePicker'
import AssistantAccessGate from './AssistantAccessGate'
import Loading from '../Loading'
import useCortexChat from './useCortexChat'

import './styles.css'

const detectEnter = (event, callback) => {
    if (!event.shiftKey && (event.keyCode === 13 || event.key === 'Enter'))
        callback()
}

// Shared presentational panel for both native chat surfaces (sidebar and
// fullTab) - see specs/cortex-native-chat/spec.md. Each surface supplies its
// own external id / lock behavior via useCortexChat's params and renders
// this the same way.
const CortexChatPanel = ({ rawExternalId, useTicketLock = false, canSendMessage = true, showKbInfoLine = true }) => {
    const chat = useCortexChat({ rawExternalId, useTicketLock, canSendMessage })

    if (!chat.ready)
        return <Loading loading nofixed />

    if (!chat.userHasAssistant)
        return <AssistantAccessGate enterpriseHasAssistant={chat.enterpriseHasAssistant} userHasAssistant={chat.userHasAssistant} />

    const inputDisabled = chat.locked || !chat.canSendMessage

    const checkEnter = (event) => {
        detectEnter(event, () => {
            event.preventDefault()
            if (chat.text !== '' && chat.activeExternalId)
                chat.submit(chat.text)
        })
    }

    return (
        <Box className="cortex-chat-panel">
            <Box className="cortex-chat-container">
                <Box className="cortex-chat-area">
                    {chat.locked &&
                        <Box className="cortex-agent-typing-container">
                            O Cortex está digitando
                        </Box>
                    }
                    <MessageList
                        list={chat.messages}
                        assistantUrl={chat.assistantUrl}
                        accessToken={chat.accessToken}
                        onAction={chat.reportAction}
                        onSend={(message) => {
                            if (window.sendMessageBack && typeof window.sendMessageBack === 'function')
                                window.sendMessageBack(message)
                            else
                                console.error('Declare window.sendMessageBack to send a Cortex answer as the next outgoing message.')
                        }}
                        onCopy={(message) => {
                            if (window.copyMessageBack && typeof window.copyMessageBack === 'function')
                                window.copyMessageBack(message)
                            else
                                console.error('Declare window.copyMessageBack to place a Cortex answer into the composer.')
                        }}
                    />
                </Box>
                {showKbInfoLine &&
                    <Box className="cortex-chat-base-informative">
                        <SchoolIcon fontSize="small" /> {chat.kbName}
                    </Box>
                }
            </Box>
            <Box className="cortex-input-area">
                <Box className="cortex-input-align-box">
                    <KnowledgeBasePicker
                        knowledgeBases={chat.knowledgeBases}
                        selectedBase={chat.selectedBase}
                        onSelect={chat.selectKb}
                        open={chat.showKbPicker}
                        onToggle={chat.setShowKbPicker}
                        packages={chat.packages}
                        maxPackages={chat.maxPackages}
                    />
                    <KnowledgeBaseButton
                        onClick={() => chat.setShowKbPicker(true)}
                        disabled={chat.locked}
                    />
                    <TextareaAutosize
                        className={`cortex-autosizer ${chat.locked ? 'searching' : ''}`}
                        minRows={1}
                        maxRows={3}
                        value={chat.text}
                        disabled={inputDisabled}
                        onKeyDown={checkEnter}
                        onChange={(e) => chat.setText(e.target.value)}
                        placeholder={!chat.locked ? 'Como posso ajudar?' : ' '}
                    />
                    <Button
                        className="cortex-send-button cortex-iconbutton sendMessage"
                        disabled={chat.text === '' || inputDisabled || !chat.activeExternalId}
                        color="primary"
                        variant="contained"
                        size="small"
                        onClick={() => chat.submit(chat.text)}
                    >
                        <SendIcon />
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export default CortexChatPanel
